import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { GlobalSearchService } from './globalsearchservice.service';
import { RuntimeConfigLoaderService } from 'runtime-config-loader';

export interface MetrcStandardResponse<T = any> {
	success?: boolean;
	error?: string;
	data?: T;
	[key: string]: any;
}

export interface MetrcHealthSummary {
	success: boolean;
	enabled: boolean;
	api_reachable: boolean;
	default_license: string;
	queue: { pending: number; running: number; failed: number; done: number };
	last_sync: Array<{
		license_number: string;
		resource_key: string;
		last_modified_cursor: string | null;
		last_success_at: string | null;
		last_error: string | null;
	}>;
	failures: Array<{
		id: number;
		job_type: string;
		license_number: string;
		attempts: number;
		last_error: string;
		updated_at: string;
	}>;
}

export interface MetrcPackage {
	id?: number;
	license_number: string;
	package_label: string;
	package_id: number;
	item_name: string | null;
	item_id: number;
	quantity: number | null;
	unit_of_measure: string | null;
	location_name: string | null;
	package_type: string | null;
	source: 'active' | 'onhold' | 'intransit' | 'inactive';
	last_seen_at: string;
	payload_json?: string | null;
	/** From list endpoint when DB columns exist */
	has_detail?: number | boolean;
	has_lab?: number | boolean;
	enriched_at?: string | null;
}

export interface MetrcPackageDetailSnapshotFields {
	id: number;
	license_number: string;
	package_label: string;
	package_id: number;
	item_name: string;
	item_id: number;
	quantity: number | string | null;
	unit_of_measure: string;
	location_name: string;
	package_type: string;
	source: string;
	last_seen_at: string;
	enriched_at?: string | null;
}

export interface MetrcPackageMappedRow {
	stkcode: string;
	metrc_item_id?: number | null;
	match_item_name?: string | null;
	license_number?: string;
}

export interface MetrcPackageDetailPayload {
	success?: boolean;
	snapshot_fields: MetrcPackageDetailSnapshotFields;
	item: Record<string, unknown> | null;
	package_merged: Record<string, unknown> | null;
	list_payload: Record<string, unknown> | null;
	detail_envelope: unknown;
	detail_body: unknown;
	lab_results: unknown;
	mapped: MetrcPackageMappedRow | null;
	live_metrc: unknown;
}

export interface MetrcTransferCacheRow {
	id: number;
	license_number: string;
	direction: 'incoming' | 'outgoing' | 'rejected';
	transfer_id: number;
	manifest_number: string | null;
	payload_json: string | null;
	synced_at: string;
	/** ERP sales order when this Metrc transfer was created from order sync */
	linked_orderno?: number | null;
}

export interface MetrcMappingCandidate {
	item_id: number;
	name: string;
	license_number: string;
	score: number;
	match_type: 'exact' | 'token';
}

export interface MetrcMappingSuggestionRow {
	stockid: string;
	description: string;
	candidates: MetrcMappingCandidate[];
	match_count: number;
	mapping_id?: number;
	mapping_mode?: string;
}

export interface MetrcSuggestScanResponse {
	success: boolean;
	license_number: string;
	scanned: number;
	auto_mapped: number;
	needs_review: number;
	unmatched: number;
	auto_mapped_rows: MetrcMappingSuggestionRow[];
	review_rows: MetrcMappingSuggestionRow[];
	unmatched_rows: MetrcMappingSuggestionRow[];
	errors: string[];
}

export interface MetrcInventoryStockmasterCandidate {
	stkcode: string;
	description: string;
	hits: number;
	token_total: number;
	score: number;
	match_type: 'strong' | 'medium' | 'weak';
}

export interface MetrcInventorySuggestionRow {
	item_id: number;
	item_name: string;
	qty_active: number;
	qty_onhold: number;
	qty_intransit: number;
	pkg_count: number;
	candidates: MetrcInventoryStockmasterCandidate[];
	match_count: number;
	mapping_id?: number;
	mapping_mode?: string;
}

export interface MetrcInventoryScanResponse {
	success: boolean;
	license_number: string;
	scanned: number;
	auto_mapped: number;
	needs_review: number;
	unmatched: number;
	auto_mapped_rows: MetrcInventorySuggestionRow[];
	review_rows: MetrcInventorySuggestionRow[];
	unmatched_rows: MetrcInventorySuggestionRow[];
	errors: string[];
}

export interface MetrcStockMappingRow {
	id?: number;
	stkcode: string;
	license_number: string;
	metrc_item_id: number | null;
	match_item_name: string | null;
	active: number;
	notes?: string | null;
	created_at?: string;
	updated_at?: string;
}

export interface MetrcOrderSyncRow {
	orderno: number;
	debtorno?: string;
	customer_name?: string;
	orddate?: string;
	license_number: string;
	state: string;
	metrc_transfer_id: string | null;
	metrc_delivery_id: string | null;
	metrc_manifest_id: string | null;
	manifest_pdf_path?: string | null;
	last_error: string | null;
	updated_at: string;
}

export interface MetrcCustomerManifestRow {
	orderno: string;
	orddate: string;
	debtorno: string;
	customer_name: string;
	recipient_license: string;
	metrc_sync_state: string;
	metrc_transfer_id: string;
	metrc_manifest_id: string;
	manifest_pdf_path: string;
	last_error: string;
	metrc_updated_at: string;
	has_manifest_pdf: boolean;
}

export interface MetrcCustomerManifestsResponse extends MetrcStandardResponse {
	debtorno?: string;
	rows?: MetrcCustomerManifestRow[];
	total?: number;
	manifest_pdf_route?: string;
	manifest_pdf_note?: string;
}

export interface MetrcRecipientLicenseStatus extends MetrcStandardResponse {
	debtorno?: string;
	name?: string;
	cosm_license?: string;
	licExpDate?: string;
	checks?: { has_recipient_license: boolean; license_expiry_ok: boolean | null; pattern_ok: boolean | null };
	seen_as_recipient_in_transfer_cache?: number;
	seen_as_recipient_in_metrc_outgoing_first_page?: boolean | null;
	metrc_outgoing_scan_error?: string | null;
	ready_for_wholesale_transfer?: boolean;
	warnings?: string[];
}

export interface MetrcReceiptSyncRow {
	id: number;
	sale_id: string;
	license_number: string;
	state: string;
	metrc_receipt_id: string | null;
	external_number: string | null;
	finalized: number;
	last_error: string | null;
	updated_at: string;
}

export interface MetrcQueueJob {
	id: number;
	job_type: string;
	license_number: string;
	status: 'pending' | 'running' | 'done' | 'failed';
	attempts: number;
	last_error: string | null;
	run_after: string;
	created_at: string;
	updated_at: string;
	payload_json?: string | null;
}

export interface MetrcRuntimeConfig {
	metrc_enabled: number;
	metrc_api_url: string;
	metrc_vendor_key: string;
	metrc_user_key_set: boolean;
	metrc_default_license: string;
	metrc_webhook_secret_set: boolean;
	updated_by: string | null;
	updated_at: string | null;
}

export interface MetrcRuntimeConfigUpdate {
	metrc_enabled?: number | boolean;
	metrc_api_url?: string;
	metrc_vendor_key?: string;
	metrc_user_key?: string;
	metrc_default_license?: string;
	metrc_webhook_secret?: string;
}

@Injectable({ providedIn: 'root' })
export class MetrcService {
	private config: any;
	private user: any = [];
	private configChangedSource = new Subject<void>();
	configChanged$ = this.configChangedSource.asObservable();

	constructor(
		private http: HttpClient,
		private globalSearchService: GlobalSearchService,
		private configSvc: RuntimeConfigLoaderService
	) {
		this.globalSearchService.configsubscription.subscribe(r => {
			this.config = r;
		});
		this.globalSearchService.user.subscribe(result => {
			this.user = result;
		});
	}

	notifyConfigChanged(): void {
		this.configChangedSource.next();
	}

	private endpoint(path: string, query: { [k: string]: any } = {}): string {
		let url = this.config.apiServer.endpoint + path + this.authQuery();
		const parts: string[] = [];
		Object.keys(query).forEach(k => {
			const v = query[k];
			if (v === null || v === undefined || v === '') return;
			parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(String(v)));
		});
		if (parts.length) url += '&' + parts.join('&');
		return url;
	}

	private authQuery(): string {
		const pkg = this.globalSearchService.getSessionItem('package');
		const userid = this.user && this.user.user ? this.user.user.userid : '';
		return '&key=' + this.config.apiCredentials.clientId + '&userid=' + userid + (pkg ? '&package=' + pkg : '');
	}

	health(): Observable<MetrcHealthSummary> {
		return this.http.get<MetrcHealthSummary>(this.endpoint('metrc/health/summary'));
	}

	pingApi(): Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/health/ping'));
	}

	listPackages(source: 'active' | 'onhold' | 'intransit', filter: { license?: string; q?: string; limit?: number; offset?: number; sort?: 'label' | 'recent' } = {}):
		Observable<{ success: boolean; rows: MetrcPackage[]; total: number; source?: string; license?: string; sort?: string; offset?: number; limit?: number }> {
		return this.http.get<{ success: boolean; rows: MetrcPackage[]; total: number; source?: string; license?: string; sort?: string; offset?: number; limit?: number }>(
			this.endpoint('metrc/packages/list', { source, ...filter })
		);
	}

	getPackageDetail(params: { id: number; license?: string; source?: string; live?: boolean }): Observable<MetrcPackageDetailPayload> {
		const q: { [k: string]: string | number } = { id: params.id };
		if (params.license) {
			q.license = params.license;
		}
		if (params.source) {
			q.source = params.source;
		}
		if (params.live) {
			q.live = 1;
		}
		return this.http.get<MetrcPackageDetailPayload>(this.endpoint('metrc/packages/detail', q));
	}

	searchPackagesByLabel(label: string, license?: string): Observable<{ success: boolean; rows: MetrcPackage[] }> {
		return this.http.get<{ success: boolean; rows: MetrcPackage[] }>(
			this.endpoint('metrc/packages/search', { label, license })
		);
	}

	getPackageLive(label: string, license?: string): Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/packages/live', { label, license }));
	}

	pullPackages(source: 'active' | 'onhold' | 'intransit', license?: string): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/packages/pull'), { source, license });
	}

	/**
	 * Split a Metrc package into 1..n new packages. Server queues a `package_create` job and the
	 * worker hits POST /packages/v2/ with Ingredients=[{ Package: source_package_label, ... }].
	 * Both the new tags and the source label get refreshed in the local snapshot afterwards.
	 */
	splitPackage(body: {
		source_package_label: string;
		splits: Array<{
			tag: string;
			quantity: number;
			unit_of_measure?: string;
			item_name?: string;
			note?: string;
			packaged_date?: string;
			production_batch_number?: string;
			is_production_batch?: boolean;
			is_donation?: boolean;
		}>;
		license?: string;
		source_item_name?: string;
		source_unit_of_measure?: string;
		packaged_date?: string;
	}): Observable<MetrcStandardResponse & { job_id?: number; deduped?: boolean }> {
		return this.http.post<MetrcStandardResponse & { job_id?: number; deduped?: boolean }>(
			this.endpoint('metrc/package_commands/split'),
			body
		);
	}

	/**
	 * Poll status of an enqueued package_* job (adjust / location / finish / unfinish / create / split).
	 * Resolves via `apir/metrc/package_commands/status`.
	 */
	getPackageJobStatus(opts: { job_id?: number; client_ref?: string; license?: string }): Observable<MetrcStandardResponse & { job?: MetrcQueueJob }> {
		return this.http.post<MetrcStandardResponse & { job?: MetrcQueueJob }>(
			this.endpoint('metrc/package_commands/status'),
			opts
		);
	}

	listTransfers(direction: 'outgoing' | 'incoming', filter: { license?: string; manifest?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcTransferCacheRow[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcTransferCacheRow[]; total: number }>(
			this.endpoint('metrc/transfers/list', { direction, ...filter })
		);
	}

	getTransfer(transferId: number, license?: string): Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/transfers/get', { transfer_id: transferId, license }));
	}

	createOutgoingTransfer(orderno: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/transfers/create'), { orderno });
	}

	getManifestPdfUrl(orderno: number): string {
		return this.endpoint('metrc/transfers/manifestPdf', { orderno });
	}

	fetchManifestPdf(orderno: number): Observable<Blob> {
		return this.http.get(this.endpoint('metrc/transfers/manifestPdf', { orderno }), { responseType: 'blob' });
	}

	/**
	 * Queue manifest PDF download. Pass ERP orderno, or Metrc transfer_id (resolved via metrc_order_sync).
	 */
	enqueueManifestPdf(opts: number | { orderno?: number; transfer_id?: number }): Observable<MetrcStandardResponse> {
		const body: { orderno?: number; transfer_id?: number } = {};
		if (typeof opts === 'number') {
			if (opts > 0) {
				body.orderno = opts;
			}
		} else {
			if (opts.orderno != null && opts.orderno > 0) {
				body.orderno = opts.orderno;
			}
			if (opts.transfer_id != null && opts.transfer_id > 0) {
				body.transfer_id = opts.transfer_id;
			}
		}
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/transfers/enqueueManifest'), body);
	}

	listMappings(filter: { stkcode?: string; license?: string; mapped?: 0 | 1; active?: 0 | 1; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcStockMappingRow[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcStockMappingRow[]; total: number }>(
			this.endpoint('metrc/mappings/list', filter)
		);
	}

	getMapping(id: number): Observable<{ success: boolean; row: MetrcStockMappingRow }> {
		return this.http.get<{ success: boolean; row: MetrcStockMappingRow }>(this.endpoint('metrc/mappings/get', { id }));
	}

	saveMapping(row: MetrcStockMappingRow): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/mappings/save'), row);
	}

	deleteMapping(id: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/mappings/delete'), { id });
	}

	bulkImportMappings(rows: MetrcStockMappingRow[]): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/mappings/bulkImport'), { rows });
	}

	suggestUnmapped(limit: number = 100): Observable<MetrcSuggestScanResponse> {
		return this.http.get<MetrcSuggestScanResponse>(this.endpoint('metrc/mappings/suggestUnmapped', { limit }));
	}

	suggestUnmappedScan(opts: { license?: string; limit?: number; include_unmatched?: 0 | 1; auto_apply?: 0 | 1 } = {}):
		Observable<MetrcSuggestScanResponse> {
		return this.http.get<MetrcSuggestScanResponse>(this.endpoint('metrc/mappings/suggest', opts));
	}

	autoMapSingleMatches(opts: { license?: string; limit?: number; include_unmatched?: 0 | 1 } = {}):
		Observable<MetrcSuggestScanResponse> {
		return this.http.post<MetrcSuggestScanResponse>(this.endpoint('metrc/mappings/autoMap'), opts);
	}

	inventorySuggestScan(opts: { license?: string; limit?: number; only_in_stock?: 0 | 1; include_unmatched?: 0 | 1; auto_apply?: 0 | 1; min_token_len?: number } = {}):
		Observable<MetrcInventoryScanResponse> {
		return this.http.get<MetrcInventoryScanResponse>(this.endpoint('metrc/mappings/inventorySuggest', opts));
	}

	autoMapInventorySingles(opts: { license?: string; limit?: number; only_in_stock?: 0 | 1; include_unmatched?: 0 | 1; min_token_len?: number } = {}):
		Observable<MetrcInventoryScanResponse> {
		return this.http.post<MetrcInventoryScanResponse>(this.endpoint('metrc/mappings/inventoryAutoMap'), opts);
	}

	searchMetrcItems(q: string, license?: string, limit: number = 100, offset: number = 0):
		Observable<{ success: boolean; rows: any[]; total: number }> {
		return this.http.get<{ success: boolean; rows: any[]; total: number }>(
			this.endpoint('metrc/mappings/searchItems', { q, license, limit, offset })
		);
	}

	searchStockmaster(q: string, limit: number = 100, offset: number = 0):
		Observable<{ success: boolean; rows: Array<{ stkcode: string; description: string }>; total: number }> {
		return this.http.get<{ success: boolean; rows: Array<{ stkcode: string; description: string }>; total: number }>(
			this.endpoint('metrc/mappings/searchStockmaster', { q, limit, offset })
		);
	}

	listOrders(filter: { state?: string; license?: string; orderno?: string; debtorno?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcOrderSyncRow[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcOrderSyncRow[]; total: number }>(
			this.endpoint('metrc/orders/list', filter)
		);
	}

	listCustomerManifests(debtorno: string, filter: { limit?: number; offset?: number } = {}): Observable<MetrcCustomerManifestsResponse> {
		return this.http.get<MetrcCustomerManifestsResponse>(this.endpoint('metrc/customers/manifests', { debtorno, ...filter }));
	}

	verifyCustomerRecipientLicense(debtorno: string, opts: { checkMetrcOutgoing?: boolean } = {}): Observable<MetrcRecipientLicenseStatus> {
		const q: { debtorno: string; check_metrc_outgoing?: number } = { debtorno };
		if (opts.checkMetrcOutgoing) {
			q.check_metrc_outgoing = 1;
		}
		return this.http.get<MetrcRecipientLicenseStatus>(this.endpoint('metrc/customers/verifyLicense', q));
	}

	getOrder(orderno: number): Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/orders/get', { orderno }));
	}

	resyncOrder(orderno: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/orders/resync'), { orderno });
	}

	cancelOrderSync(orderno: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/orders/cancel'), { orderno });
	}

	listReceipts(filter: { state?: string; license?: string; sale_id?: string; finalized?: 0 | 1; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcReceiptSyncRow[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcReceiptSyncRow[]; total: number }>(
			this.endpoint('metrc/receipts/list', filter)
		);
	}

	pushReceipt(saleId: string): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/receipts/push'), { sale_id: saleId });
	}

	finalizeReceipt(saleId: string): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/receipts/finalize'), { sale_id: saleId });
	}

	unfinalizeReceipt(saleId: string): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/receipts/unfinalize'), { sale_id: saleId });
	}

	enqueueReceipt(saleId: string, type: 'push' | 'finalize' | 'unfinalize' = 'push'): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/receipts/enqueue'), { sale_id: saleId, type });
	}

	listQueue(filter: { status?: string; job_type?: string; license?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: MetrcQueueJob[]; total: number }> {
		return this.http.get<{ success: boolean; rows: MetrcQueueJob[]; total: number }>(
			this.endpoint('metrc/queue/list', filter)
		);
	}

	queueStats(): Observable<{ success: boolean; counts: { pending: number; running: number; failed: number; done: number } }> {
		return this.http.get<{ success: boolean; counts: { pending: number; running: number; failed: number; done: number } }>(
			this.endpoint('metrc/queue/stats')
		);
	}

	retryJob(id: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/queue/retry'), { id });
	}

	killJob(id: number): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/queue/kill'), { id });
	}

	enqueueJob(jobType: string, payload: any = {}, license?: string): Observable<MetrcStandardResponse> {
		const body: any = { job_type: jobType, payload };
		if (license !== undefined && license !== null && String(license).trim() !== '') {
			body.license = String(license).trim();
		}
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/queue/enqueue'), body);
	}

	purgeDoneJobs(olderThanDays: number = 30): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/queue/purgeDone'), { older_than_days: olderThanDays });
	}

	listItems(filter: { license?: string; q?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: any[]; total: number }> {
		return this.http.get<{ success: boolean; rows: any[]; total: number }>(this.endpoint('metrc/reference/items', filter));
	}

	/** Synchronous pull from Metrc → DB cache, then returns first page of cached rows. */
	pullItemsFromMetrc(body: { license?: string; q?: string; limit?: number } = {}): Observable<{
		success: boolean;
		pulled?: boolean;
		items_synced?: number;
		license?: string;
		rows?: any[];
		total?: number;
		error?: string;
		message?: string;
	}> {
		return this.http.post<{
			success: boolean;
			pulled?: boolean;
			items_synced?: number;
			license?: string;
			rows?: any[];
			total?: number;
			error?: string;
			message?: string;
		}>(this.endpoint('metrc/reference/pullItems'), body);
	}

	listLocations(filter: { license?: string; q?: string; limit?: number; offset?: number } = {}):
		Observable<{ success: boolean; rows: any[]; total: number }> {
		return this.http.get<{ success: boolean; rows: any[]; total: number }>(this.endpoint('metrc/reference/locations', filter));
	}

	liveReference(kind: 'unitsofmeasure' | 'transfertypes' | 'adjustreasons' | 'labstates' | 'customertypes' | 'paymenttypes', license?: string):
		Observable<MetrcStandardResponse> {
		return this.http.get<MetrcStandardResponse>(this.endpoint('metrc/reference/live', { kind, license }));
	}

	getRuntimeConfig(): Observable<{ success: boolean; config: MetrcRuntimeConfig }> {
		return this.http.get<{ success: boolean; config: MetrcRuntimeConfig }>(this.endpoint('metrc/config/get'));
	}

	saveRuntimeConfig(update: MetrcRuntimeConfigUpdate): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/config/save'), update);
	}

	testRuntimeConfig(update?: MetrcRuntimeConfigUpdate): Observable<MetrcStandardResponse> {
		return this.http.post<MetrcStandardResponse>(this.endpoint('metrc/config/test'), update || {});
	}
}
